// shared/analytics/pathTemplate.ts
/**
 * Raw path → route TEMPLATE. The tracker sends only this; the collector applies it AGAIN and
 * never trusts the client (spec §1.1 "Path privacy", review blocker 1).
 *
 *  1. Query string and #fragment are always stripped.
 *  2. An ordered allowlist of known tokenised routes maps a path to its template.
 *  3. Otherwise every token-looking segment becomes ':x'.
 *
 * ⚠️ Spec deviation, deliberate: "≥16 chars of [A-Za-z0-9_-]" alone would redact every
 * la-mirage.in page slug ("destination-wedding-in-kerala"). A segment made only of lower-case
 * words joined by hyphens is kept; a random token of that shape is vanishingly unlikely, and
 * anything with a digit, upper case or underscore is still redacted.
 */
export const ROUTE_TEMPLATES: readonly string[] = [
  '/rsvp/:token', '/invite/:token', '/guest-photos/upload/:token', '/photos/gallery/:token',
  '/live-photos/gallery/:token', '/live-photos/register/:token', '/live-photos/slideshow/:weddingId',
  '/valet/request/:token', '/v/:kind/:token', '/van/day/:token', '/w/:slug',
  '/wedding-website/:id', '/wedding-website-public/:id', '/inv/:token', '/sign/:token',
  '/vr/:eventCode', '/vr-display/:eventId', '/meeting/guest/:roomName', '/gallery/:slug',
  '/booth/:slug', '/concierge/:weddingId', '/menu/:weddingId',
];

const MAX_LEN = 200;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const WORD_SLUG = /^[a-z]+(-[a-z]+)*$/;
const SPLIT = ROUTE_TEMPLATES.map((t) => t.split('/').filter(Boolean));

export function isTokenish(seg: string): boolean {
  if (!seg || seg.startsWith(':')) return false;
  if (UUID.test(seg)) return true;
  if (/\d{6,}/.test(seg)) return true;
  if (seg.includes('@') || seg.toLowerCase().includes('%40')) return true;
  return seg.length >= 16 && /^[A-Za-z0-9_-]+$/.test(seg) && !WORD_SLUG.test(seg);
}

export function pathTemplate(raw: unknown): string {
  if (typeof raw !== 'string') return '/';
  let p = raw.trim();
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(p)) {
    try { p = new URL(p).pathname; } catch { return '/'; }
  }
  const cut = p.search(/[?#]/);
  if (cut !== -1) p = p.slice(0, cut);
  if (!p.startsWith('/')) p = `/${p}`;
  p = p.replace(/\/{2,}/g, '/');
  const trailing = p.length > 1 && p.endsWith('/');
  const segs = p.split('/').filter(Boolean);

  for (let i = 0; i < SPLIT.length; i++) {
    const t = SPLIT[i];
    if (t.length === segs.length && t.every((x, j) => x.startsWith(':') || x === segs[j])) return ROUTE_TEMPLATES[i];
  }
  const out = `/${segs.map((s) => (isTokenish(s) ? ':x' : s)).join('/')}${trailing && segs.length ? '/' : ''}`;
  return out.length > MAX_LEN ? out.slice(0, MAX_LEN) : out;
}
